import type { Task } from "../tasks/types.js";
import type { RecurrenceRule } from "../recurrence/types.js";
import type { RecurrenceOverride } from "../recurrence/overrides/types.js";
import type { Label } from "../labels/types.js";
import type { Topic } from "../topics/types.js";
import type { Calendar } from "../calendars/types.js";
import type { ThemeConfig } from "../theme/types.js";

/** Bump when the envelope shape changes incompatibly; `parseBackup` rejects unknown versions. */
export const BACKUP_VERSION = 1 as const;

/**
 * A curated, JSON-safe subset of UI preferences carried in a backup. Kept
 * deliberately narrow — NEVER include the Anthropic API key or other secrets.
 * All fields optional so old backups (and partial writes) still parse; the app
 * validates each field per-key on restore.
 */
export interface BackupPreferences {
  /** Keyboard-shortcut bindings (action → key). */
  shortcuts?: Record<string, string>;
  /** Order of the sidebar filter sections. */
  sidebarFilterOrder?: string[];
  /** Order of the top-level sidebar parts. */
  sidebarNavOrder?: string[];
  /** Which optional sidebar parts are shown. */
  sidebarVisible?: Record<string, boolean>;
  /** Manual Priorities-nav visibility; null follows the matrix-scope default. */
  prioritiesNavVisibleOverride?: boolean | null;
  /** Which sidebar sections are collapsed. */
  sidebarCollapsed?: Record<string, boolean>;
  /** User-set entry order (ids) within each sidebar list. */
  topicOrder?: string[];
  labelOrder?: string[];
  calendarOrder?: string[];
  /** Route-specific topic/calendar exclusions for Month, Today, and Priorities views. */
  monthExcludedTopicIds?: string[];
  monthExcludedCalendarIds?: string[];
  todayExcludedTopicIds?: string[];
  todayExcludedCalendarIds?: string[];
  /** Topics hidden from the entire Priorities view (board, queue, and load strip). */
  prioritiesExcludedTopicIds?: string[];
  /** Per-parent child-topic order (key = parent id). */
  topicChildOrder?: Record<string, string[]>;
  // --- Non-secret app settings (NEVER the Anthropic API key). Typed loosely
  //     (plain strings/numbers) so core stays free of web/ui union types; the
  //     app validates each on apply. ---
  weekStartsOn?: number;
  /** Default left edge for Week and X-Days current-period focus. */
  multiDayFocus?: string;
  defaultTz?: string | null;
  defaultCalendarId?: string | null;
  timeFormat?: string;
  defaultView?: string;
  defaultReminderMinutes?: number[];
  /** Reminder lead-times seeded onto NEW events (tasks use defaultReminderMinutes). */
  defaultEventReminderMinutes?: number[];
  /** Default duration (minutes) for a new timed entry without an implied length. */
  defaultDurationMin?: number;
  /** Whether new entries default to an event (vs a task). */
  defaultEntryIsEvent?: boolean;
  snoozeMorningHour?: number;
  snoozeEveningHour?: number;
  /** Local hour the Priorities time-ladder "afternoon" rung anchors to. */
  snoozeAfternoonHour?: number;
  /** Days the Today agenda shows (incl. today): 3, 7 or 14. */
  agendaDays?: number;
  /** Whether Today renders upcoming days with nothing scheduled. */
  agendaShowEmptyDays?: boolean;
  theme?: string;
  /** App language for localized UI surfaces. */
  language?: string;
  /** App-wide text-size scale. */
  textScale?: number;
  /** App-wide font preset. */
  textFont?: string;
  lastCalendarView?: string;
  subtaskRollup?: string;
  /** Where the 3×3 priority/urgency matrix is enabled: "off" (nowhere — forms
   *  always use flags), "tasks" (tasks only) or "tasks_events" (everywhere). */
  matrixScope?: string;
  /** Auto-populate the matrix urgency axis from a task's deadline. */
  deadlineUrgency?: boolean;
  /** Day-distance thresholds for deadline-derived urgency (high/medium cutoffs). */
  deadlineUrgencyHighDays?: number;
  deadlineUrgencyMediumDays?: number;
  calendarHourHeight?: number;
  calendarDayCount?: number;
  blockTintDiff?: number;
  blockGradient?: boolean;
  sidebarWidth?: number;
  sidebarOpen?: boolean;
  /** Mobile create/edit sheet: how topic buttons render. */
  mobileTopicDisplay?: string;
  /** Create/edit forms: allow choosing multiple topics. */
  mobileTopicMulti?: boolean;
  /** Desktop create/edit dialog skin: "paper" (classic) or "modern" (v2). */
  desktopFormVariant?: string;
  /** Mobile create/edit sheet: partial-state height (fraction of viewport). */
  mobileSheetHeight?: number;
  /** Mobile: open the feedback composer automatically after a system screenshot. */
  mobileScreenshotFeedback?: boolean;
  /** Mobile: show the swipe hint bar at the top of calendar views. */
  showCalendarSwipeHint?: boolean;
  /** First-run onboarding has been completed/dismissed. */
  onboardingComplete?: boolean;
  /** Current onboarding step, so setup can resume on another synced device. */
  onboardingStep?: string | null;
}

/** Singleton sync row carrying the curated, non-secret preferences block. */
export interface AppPreferencesRecord extends BackupPreferences {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * The full exportable data graph. Carries tasks/events plus everything they
 * reference (recurrence rules + overrides + labels), plus an optional curated
 * `preferences` block. Deliberately excludes settings secrets — we never write
 * the Anthropic API key to a plain file.
 */
export interface BackupEnvelope {
  version: typeof BACKUP_VERSION;
  /** ISO-8601 instant the file was written. */
  exportedAt: string;
  tasks: Task[];
  recurrenceRules: RecurrenceRule[];
  recurrenceOverrides: RecurrenceOverride[];
  labels: Label[];
  /** Topic entities tasks reference via `topic_ids`. Absent in older backups. */
  topics: Topic[];
  /** Calendar entities tasks reference via `calendar_id`. Absent in older backups. */
  calendars: Calendar[];
  /** Active/custom theme rows. Absent in older backups. */
  themes?: ThemeConfig[];
  /** Curated UI preferences (no secrets). Absent in pre-this-version backups. */
  preferences?: BackupPreferences;
}
